/**
 * Staggered cantilever (family 2, research/taxonomy.md §1.4, plan §4.7): an open stack of
 * tiers with no continuous outer sides; end panels and posts change tier by tier, shelves run
 * past their supports to free ends, one continuous divider runs up the middle.
 */
import { frontsParameterDef } from '../fronts';
import type { FamilyDefinition } from '../types';
import { mm } from '../../domain/units';
import { baseParameterDefs } from '../base';
import { STAGGERED_ID, STAGGERED_LABEL, STAGGERED_PRESETS, STAGGERED_STOCK, STAGGERED_VERSION } from './design';

export const STAGGERED_DEFINITION: FamilyDefinition = Object.freeze({
  id: STAGGERED_ID,
  version: STAGGERED_VERSION,
  label: STAGGERED_LABEL,
  description: 'An open S or zig-zag stack: end panels alternate tier by tier and shelves overhang to free ends.',
  parameters: [
    { key: 'W', type: 'length', fieldKey: 'W', range: { min: mm(400), max: mm(1600), step: 10 }, default: mm(1000), unit: 'mm', critical: true, ui: { label: 'Width', control: 'field', group: 'Overall size' } },
    { key: 'H', type: 'length', fieldKey: 'H', range: { min: mm(500), max: mm(2100), step: 10 }, default: mm(1800), unit: 'mm', critical: true, ui: { label: 'Height', control: 'field', group: 'Overall size' } },
    { key: 'D', type: 'length', fieldKey: 'D', range: { min: mm(200), max: mm(400), step: 10 }, default: mm(300), unit: 'mm', critical: true, ui: { label: 'Depth', control: 'field', group: 'Overall size' } },
    {
      key: 'tiers', type: 'count', range: { min: 2, max: 8, step: 1 }, default: 5, unit: 'count', critical: false,
      ui: { label: 'Tiers', help: 'Changing the count refills every tier with the alternate pattern, equal heights.', control: 'stepper', group: 'Tiers' },
      behaviours: ['alternate'],
    },
    {
      key: 'preset', type: 'enum', default: 'alternate', critical: false,
      options: [
        { value: 'alternate', label: 'Alternate', help: 'End panels zig-zag tier by tier; shelves step out to free ends (the S-shelf).' },
        { value: 'free', label: 'As set', help: 'Each tier keeps the ends set below.' },
      ],
      ui: { label: 'Pattern', control: 'choice', group: 'Tiers' },
    },
    {
      key: 'tierEnds', type: 'perTier', default: null, critical: false,
      ui: { label: 'Tier ends', help: 'Per tier, bottom to top: end panels, posts and the back block. Panels that line up with no shelf between become one board.', control: 'rows', group: 'Tiers' },
    },
    {
      key: 'verticalKind', type: 'enum', default: 'post', critical: false,
      options: [
        { value: 'panel', label: 'Full-depth panels', help: 'Inner uprights run the full depth of the shelves.' },
        { value: 'post', label: 'Slim posts', help: 'Inner uprights are slim posts at the front.' },
      ],
      ui: { label: 'Inner uprights', control: 'choice', group: 'Tiers' },
    },
    {
      key: 'tierDividers', type: 'list', default: null, critical: false,
      ui: { label: 'Dividers per tier', help: 'Per tier, bottom to top: where each short full-depth divider stands (fractions of the width).', control: 'rows', group: 'Tiers' },
    },
    ...baseParameterDefs('Base', { sides: false }),
    frontsParameterDef(),
    {
      key: 'stock', type: 'enum', fieldKey: 'stockThickness', default: STAGGERED_STOCK, critical: true,
      options: [
        { value: 'sheesham-board-25', label: 'Sheesham 25 mm' },
        { value: 'oak-board-20', label: 'Oak 20 mm' },
        { value: 'sheesham-board-38', label: 'Sheesham 38 mm' },
        { value: 'sheesham-board-45', label: 'Sheesham 45 mm' },
        { value: 'oak-board-38', label: 'Oak 38 mm' },
        { value: 'oak-board-45', label: 'Oak 45 mm' },
        { value: 'sheesham-board-70', label: 'Sheesham 70 mm slab' },
        { value: 'oak-board-70', label: 'Oak 70 mm slab' },
        { value: 'birch-ply-18', label: 'Birch plywood 18 mm' },
      ],
      ui: { label: 'Material', help: 'Chosen in Look. Solid timber wider than the boards is glued up.', control: 'choice', group: 'Construction', step: 'look' },
    },
  ],
  presets: (Object.entries(STAGGERED_PRESETS) as [string, (typeof STAGGERED_PRESETS)[keyof typeof STAGGERED_PRESETS]][]).map(([id, p]) => ({ id, label: p.label, description: p.description, params: { W: p.W, H: p.H, D: p.D, tiers: p.tiers } })),
  handles: [],
}) as FamilyDefinition;
